/// <reference types="chrome"/>
import { EventEmitter, Injectable, Output } from "@angular/core";
import { environment } from "src/environments/environment";
import { Browser, detect } from "detect-browser";
import { SharedDataService } from "./shared-data.service";
import { MasqueradeUserResponse } from "src/app/core/models/masquerade.model";

@Injectable()
export class ExtensionService {
  @Output() stopBanner = new EventEmitter<any>();

  /** True when running in a Chromium browser that exposes `window.chrome` with Google's vendor string. */
  isChromeBrowser = window.chrome && window.navigator.vendor === "Google Inc.";
  /** Truthy when `chrome.runtime` is available (i.e. extension messaging is possible). */
  isChromeRuntime = this.isChromeBrowser && chrome.runtime;
  browser_type: Browser;

  /** Chrome Web Store listing for the FlyMSG extension. */
  readonly CHROME_WEB_STORE_URL =
    "https://chrome.google.com/webstore/detail/fly-msg-for-chrome/giidlnpcdhcldhfccdhkaicefhpokghc?hl=en&authuser=0";

  /** Microsoft Edge Add-ons listing for the FlyMSG extension. */
  readonly EDGE_ADDONS_STORE_URL =
    "https://microsoftedge.microsoft.com/addons/detail/flymsg-text-expansion-to/mmgiiedikndnbnjnhpgjdgoacimlhdkn";

  /**
   * The resolved extension ID for the current session.
   * Set after the first successful `isExtensionInstalled()` check.
   * Can be either the production ID or the testing ID (for beta users).
   */
  private _activeExtensionId: string | null = null;

  constructor(private readonly sharedData: SharedDataService) {
    this.browser_type = this.sharedData.getAttribute("browser_type") as Browser;
    // Store browser type
    var browser = detect();
    if (browser && (browser?.name == "edge" || browser?.name == "edge-chromium")) {
      this.browser_type = "edge";
    } else {
      this.browser_type = browser?.name as Browser;
    }

    if (this.browser_type) {
      this.sharedData.setAttribute("browser_type", this.browser_type);
    }
  }

  /**
   * Display name of the detected browser, normalized for UI copy:
   * "edge"/"edge-chromium" resolve to "Edge", any other browser resolves to
   * its detected name (e.g. "chrome", "firefox").
   */
  get browserName(): string {
    const type = this.browser_type as string;
    return type === "edge" || type === "edge-chromium" ? "Edge" : type;
  }

  /**
   * Opens the browser-appropriate web store page for the FlyMSG extension in a
   * new tab: the Edge Add-ons page on Edge, the Chrome Web Store page for every
   * other browser. Optional `windowFeatures` are forwarded to `window.open`
   * for call sites that open the page as a sized popup instead of a plain tab.
   */
  openExtensionStorePage(windowFeatures?: string): void {
    const site = this.browser_type === "edge" ? this.EDGE_ADDONS_STORE_URL : this.CHROME_WEB_STORE_URL;
    window.open(site, "_blank", windowFeatures);
  }

  private get isBetaUser(): boolean {
    return this.userDetails?.user_details?.is_beta === true;
  }

  private checkByImage(extensionId: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = `chrome-extension://${extensionId}/images/fm.png`;
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
    });
  }

  private async checkByMessage(extensionId: string): Promise<boolean> {
    const timeout = new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 1000));

    try {
      return await Promise.race([
        this.sendExtensionMessageWithId<boolean>(extensionId, { request: "exists" }),
        timeout,
      ]);
    } catch {
      return false;
    }
  }

  /**
   * Tests both image-load and message checks for a given extension ID.
   */
  private async tryExtensionId(extensionId: string): Promise<boolean> {
    const [byImage, byMessage] = await Promise.all([
      this.checkByImage(extensionId),
      this.checkByMessage(extensionId),
    ]);
    return byImage || byMessage;
  }

  /**
   * Checks whether the extension is installed.
   * Tries the production extension ID first. If that fails and the user has
   * `is_beta: true`, falls back to `EXTENSION_ID_TESTING`.
   * The working ID is cached in `_activeExtensionId` for the session.
   *
   * Pass `{ fresh: true }` (issue #518) to bypass the `_activeExtensionId`
   * session short-circuit below and force a real re-probe — needed by
   * callers that poll for a live install (the walk-me modal's install step)
   * or re-check right before acting on a possibly-stale "not installed"
   * assumption (the welcome nag's "Remind Me" re-open). A `fresh` check that
   * DOES find the extension still (re-)populates `_activeExtensionId`, so
   * later non-fresh calls keep benefiting from the cache.
   */
  async isExtensionInstalled(options?: { fresh?: boolean }): Promise<boolean> {
    // Return immediately if already resolved for this session — unless the
    // caller explicitly asked for a fresh probe.
    if (!options?.fresh && this._activeExtensionId) {
      return true;
    }

    let productionId: string;
    try {
      productionId = this.getBrowserExtensionId(this.browser_type);
    } catch {
      return false;
    }

    // Try the production extension first
    if (await this.tryExtensionId(productionId)) {
      this._activeExtensionId = productionId;
      return true;
    }

    // For beta users, try the testing extension as a fallback
    if (this.isBetaUser && environment.EXTENSION_ID_TESTING) {
      const testingId = environment.EXTENSION_ID_TESTING;
      if (await this.tryExtensionId(testingId)) {
        this._activeExtensionId = testingId;
        return true;
      }
    }

    return false;
  }

  async isExtensionSignedIn(): Promise<boolean> {
    try {
      const response = await this.sendExtensionMessage<{
        hasAccessToken: boolean;
      }>({
        request: "hasAccessToken",
      });

      if (!response.hasAccessToken) {
        const url = "v1/user/auth/login/extension_sync";
        let userDetails = this.userDetails;

        let data = {
          accessToken: this.accessToken,
          refreshToken: this.refreshToken,
          hubspot_id: userDetails.user_details.hubspot_id,
          email: userDetails.user_details.email,
          userDetails,
        };
        return await this.sendLoginMessage(url, data);
      }

      return true;
    } catch (e) {
      return false;
    }
  }

  getBrowserExtensionId(browser: Browser) {
    switch (browser) {
      case "edge":
      case "edge-chromium":
        return environment.EDGE_EXTENSION_ID;
      case "opera":
        return environment.OPERA_EXTENSION_ID;
      case "chrome":
        return environment.EXTENSION_ID;
      default:
        throw new Error("Browser not supported for FlyMSG extension");
    }
  }

  sendLogoutMessage() {
    this.sendExtensionMessage({
      request: "logout",
    })
      .then(() => {
        this.sharedData.setAttribute("is_extention_avialble", "no");
      })
      // No extension installed — nothing to log out of.
      .catch(() => {
        this.sharedData.setAttribute("is_extention_avialble", "no");
      });
  }

  async updateUserDetails(userDetails) {
    try {
      const response = await this.sendExtensionMessage<boolean>({
        request: "update_user",
        data: userDetails,
      });

      return response;
    } catch {
      // No extension installed — nothing to update.
      return false;
    }
  }

  async sendLoginMessage(url: string, data: any) {
    const user = this.sharedData.getAttribute("userDetails");

    if (!user.user_details.email_verified_at) {
      return false;
    }

    this.sharedData.setAttribute("is_extention_avialble", "yes");

    try {
      const response = await this.sendExtensionMessage({
        url,
        data,
        request: "login",
      });

      if (response) {
        this.sharedData.setAttribute("is_extention_avialble", "yes");
        return true;
      } else {
        this.sharedData.setAttribute("is_extention_avialble", "no");
        return false;
      }
    } catch {
      this.sharedData.setAttribute("is_extention_avialble", "no");
      return false;
    }
  }

  sendUpdateMessage() {
    // Fire-and-forget; without the .catch an absent extension surfaces as an
    // unhandled rejection ("ERROR Failed to send message to extension").
    this.sendExtensionMessage({
      request: "update",
    }).catch(() => {});
  }

  // REMOVED: sendMasqueradeUser() - replaced by MasqueradeService
  // Extension communication now handled directly by MasqueradeService

  sendLoginEvent() {
    const url = "v1/user/auth/login/extension_sync";
    let userDetails = this.userDetails;

    let data = {
      accessToken: this.accessToken,
      refreshToken: this.refreshToken,
      hubspot_id: userDetails.user_details.hubspot_id,
      email: userDetails.user_details.email,
      userDetails,
    };
    this.sendLoginMessage(url, data);
  }

  sendLogoutEvent() {
    this.sendLogoutMessage();
  }

  async getAccessTokenFromExtension(): Promise<string | null> {
    try {
      const response = await this.sendExtensionMessage<{ access_token: string } | null>({
        request: "getAccessToken",
      });
      return response?.access_token ?? null;
    } catch {
      return null;
    }
  }

  async getVersion() {
    const response = await this.sendExtensionMessage<{ version: number }>({
      request: "version",
    });

    return response.version;
  }

  /**
   * Core message sender that targets a specific extension ID.
   * Retries up to 10 times while the Chrome runtime API is not yet available.
   */
  private sendExtensionMessageWithId<T>(extensionId: string, data: any): Promise<T> {
    let retryAttempts: number = 10;
    return new Promise((resolve, reject) => {
      function sendMessageToExtension() {
        if (retryAttempts === 0) {
          reject("Failed to send message to extension");
          return;
        }

        if (typeof chrome !== "undefined" && chrome.runtime && chrome.runtime.sendMessage) {
          chrome.runtime.sendMessage(extensionId, data, (response) => {
            if (chrome.runtime.lastError) {
              reject(chrome.runtime.lastError);
            } else {
              if (data.request === "exists") {
                resolve(Boolean(response) as T);
              } else {
                resolve(response);
              }
            }
          });
        } else {
          retryAttempts--;
          setTimeout(sendMessageToExtension, 1000);
        }
      }

      sendMessageToExtension();
    });
  }

  /**
   * Sends a message to the active extension.
   * Uses the cached `_activeExtensionId` when available (resolved by
   * `isExtensionInstalled()`). Falls back to the production ID otherwise.
   */
  public sendExtensionMessage<T>(data: any): Promise<T> {
    const crx = this._activeExtensionId ?? this.getBrowserExtensionId(this.browser_type);
    return this.sendExtensionMessageWithId<T>(crx, data);
  }

  // REMOVED: Duplicate getters (masqueradeUser, accessToken, refreshToken, userDetails)
  // These are already available in AuthService and should be accessed from there
  // to avoid multiple sources of truth.
  //
  // Use AuthService.masqueradeUser, AuthService.accessToken, etc. instead

  get accessToken(): string {
    // Delegate to shared data - masquerade logic handled in AuthService
    return this.sharedData.getAttribute("accessToken");
  }

  get refreshToken(): string | null {
    return this.sharedData.getAttribute("refreshToken");
  }

  get userDetails() {
    return this.sharedData.getAttribute("userDetails");
  }
}
